import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { restaurantOrderService } from '../../../../services/restaurantOrderService';
import { RestaurantOrderListDTO } from '../../../../types';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';
import '../RestaurantPage.css';

const STATUS_COLORS: Record<string, string> = {
  Open: '#3b82f6', Sent: '#8b5cf6', InPreparation: '#f59e0b',
  Ready: '#10b981', Completed: '#6b7280', Voided: '#ef4444',
};
const ORDER_STATUSES = ['', 'Open', 'Sent', 'InPreparation', 'Ready', 'Completed', 'Voided'];

const RestaurantOrdersListPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<RestaurantOrderListDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => { loadOrders(); }, [page, statusFilter]); // eslint-disable-line

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await restaurantOrderService.getAll(page, pageSize, statusFilter || undefined);
      const d = (res.data as any)?.data;
      if (d?.data) {
        setOrders(d.data);
        setTotal(d.pagination?.total ?? 0);
      } else if (Array.isArray(d)) {
        setOrders(d);
        setTotal(d.length);
      }
    } catch { setError('Failed to load orders.'); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="rest-list-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/order')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">Order History</div>
          </div>
          <div className="ims-top-bar-right">
            <select className="ims-form-control" style={{ width: '160px', height: '34px', fontSize: '13px' }}
              value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {ORDER_STATUSES.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ marginTop: '1rem' }}>{error}</div>}

        <div style={{ marginTop: '1rem', background: 'var(--ims-bg-card, #fff)', borderRadius: '8px', border: '1px solid var(--ims-border, #e5e7eb)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <Loader2 size={24} className="spin" />
            </div>
          ) : orders.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--ims-text-sub)' }}>No orders found.</div>
          ) : (
            <table className="ims-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Order #','Outlet','Type','Table','Customer','Status','Total','Date',''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', background: 'var(--ims-bg-th, #f9fafb)', fontSize: '11px', fontWeight: 600, color: 'var(--ims-text-sub)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={{ borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: '13px' }}>{o.orderNumber}</td>
                    <td style={{ padding: '10px 14px', fontSize: '13px' }}>{o.outletName || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: '13px' }}>{o.orderType}</td>
                    <td style={{ padding: '10px 14px', fontSize: '13px' }}>{o.tableNumber || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: '13px' }}>{o.customerName || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: `${STATUS_COLORS[o.status] ?? '#6b7280'}22`, color: STATUS_COLORS[o.status] ?? '#6b7280', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600 }}>
                        {o.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: '13px' }}>${Number(o.totalAmount).toFixed(2)}</td>
                    <td style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--ims-text-sub)' }}>
                      {new Date(o.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button className="ims-btn ims-btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}
                        onClick={() => navigate(`/restaurant/orders/${o.id}`)}>
                        <Eye size={12} style={{ marginInlineEnd: 4 }} />View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1rem' }}>
            <button className="ims-btn ims-btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span style={{ fontSize: '13px', padding: '6px 8px' }}>Page {page}</span>
            <button className="ims-btn ims-btn-secondary" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RestaurantOrdersListPage;
